/**
 * NoShowTimelineCard — instructor-visible audit + reversal surface
 * (Feb 18, 2026 — Authority + Consumer Wiring sprint, residual P0).
 *
 * Calm chronological list of recent no-show records belonging to
 * the current instructor (admin sees all). Each item shows what
 * was recorded, when, and the reason. Within the 24h reversal
 * window the consumer may tap "Undo no-show" — a two-step calm
 * flow handled inline.
 *
 * Doctrine references:
 *   /app/memory/NO_SHOW_DOCTRINE_SPEC.md  (§5b reversal window)
 *   /app/memory/PLATFORM_AUTHORITY_DOCTRINE.md
 *
 * What this surface does:
 *   - Reads /api/no-show/recent (audit rows + computed
 *     `reversal_window_open` flag from server — no client floor
 *     calculation, single source of truth).
 *   - Surfaces an "Undo no-show" affordance ONLY when the server
 *     said the window is still open AND the row hasn't already
 *     been reversed.
 *   - Two-step confirm before POSTing to /api/no-show/reverse.
 *   - Refetches the list on success.
 *
 * What this surface does NOT do:
 *   - Does NOT delete history. Reversal appends a NEW audit row
 *     (append-only invariant).
 *   - Does NOT surface the learner's name or contact details
 *     (privacy — only the snapshot's `pupil_id` token).
 *   - Does NOT call any AI service.
 *   - Does NOT trigger refunds, charging, notifications, or any
 *     downstream automation.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { UserX, RotateCcw, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatTimeOnly(iso) {
  if (!iso) return "";
  // Handles full ISO ("2026-05-18T02:04:38…") AND plain "HH:MM" tokens.
  if (/^\d{1,2}:\d{2}$/.test(String(iso))) return iso;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const REASON_LABEL = {
  no_arrival: "Pupil did not arrive",
  no_response: "Pupil did not respond",
  instructor_present_pupil_absent: "I was on-site, pupil wasn't",
  other: "Other",
  reversed_by_instructor: "Reversed by instructor",
  amendment: "Audit amendment",
};

export default function NoShowTimelineCard({ days = 30 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingReversalId, setConfirmingReversalId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/no-show/recent?days=${encodeURIComponent(days)}`,
        { withCredentials: true },
      );
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  async function reverse(lessonEventId) {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/no-show/reverse`,
        { lesson_event_id: lessonEventId },
        { withCredentials: true },
      );
      toast.success("No-show reversed — lesson moved back to cancelled.");
      setConfirmingReversalId(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reverse no-show.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid="no-show-timeline-loading"
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5"
      >
        <p className="text-sm text-slate-400">Loading no-show audit…</p>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div
        data-testid="no-show-timeline-empty"
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5"
      >
        <header className="flex items-center gap-2 mb-1">
          <UserX className="w-4 h-4 text-slate-400" />
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
            No-show audit
          </p>
        </header>
        <p className="text-[12px] text-slate-500 dark:text-slate-400">
          No no-shows recorded in the last {days} days.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="no-show-timeline-card"
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5"
    >
      <header className="flex items-center gap-2 mb-3">
        <UserX className="w-4 h-4 text-slate-400" />
        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
          No-show audit ({items.length})
        </p>
      </header>

      <ul className="space-y-3">
        {items.map((it) => {
          const isReversal = it.is_reversal;
          const canReverse = it.reversal_window_open;
          const reversed = it.already_reversed;
          const reasonLabel = REASON_LABEL[it.reason] || it.reason;
          const dateLabel = formatDateTime(it.recorded_at);
          const snap = it.lesson_snapshot || {};
          const isConfirming = confirmingReversalId === it.lesson_event_id;
          return (
            <li
              key={it.id}
              data-testid={`no-show-timeline-row-${it.id}`}
              className={`relative pl-4 ${
                isReversal
                  ? "border-l-2 border-slate-300 dark:border-slate-700"
                  : "border-l-2 border-rose-300 dark:border-rose-700/60"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-bold ${
                      isReversal
                        ? "text-slate-500 dark:text-slate-400"
                        : "text-slate-900 dark:text-white"
                    }`}
                  >
                    {isReversal ? "Reversal" : reasonLabel}
                  </p>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
                    <Clock className="inline w-3 h-3 mr-1 -mt-0.5" />
                    {dateLabel}
                    {snap.date && (
                      <>
                        {" · lesson "}
                        <span data-testid={`no-show-row-lesson-date-${it.id}`}>
                          {snap.date}
                        </span>
                        {snap.start_time ? ` ${formatTimeOnly(snap.start_time)}` : ""}
                      </>
                    )}
                  </p>
                  {it.notes && !isReversal && (
                    <p
                      data-testid={`no-show-row-notes-${it.id}`}
                      className="text-[12px] text-slate-600 dark:text-slate-300 mt-1 italic"
                    >
                      &ldquo;{it.notes}&rdquo;
                    </p>
                  )}
                  {reversed && !isReversal && (
                    <p
                      data-testid={`no-show-row-already-reversed-${it.id}`}
                      className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mt-1"
                    >
                      Already reversed
                    </p>
                  )}
                </div>

                {canReverse && !isConfirming && (
                  <button
                    type="button"
                    data-testid={`no-show-reverse-open-${it.lesson_event_id}`}
                    onClick={() => setConfirmingReversalId(it.lesson_event_id)}
                    className="shrink-0 inline-flex items-center gap-1 text-[12px] font-bold text-slate-700 dark:text-slate-200 px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Undo no-show
                  </button>
                )}
              </div>

              {isConfirming && (
                <div
                  data-testid={`no-show-reverse-confirm-${it.lesson_event_id}`}
                  className="mt-3 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-3"
                >
                  <p className="text-[12px] text-amber-800 dark:text-amber-200 mb-2">
                    Reverse this no-show? The original record stays on
                    file (append-only). The lesson moves back to
                    cancelled. The pupil is <span className="font-bold">not</span> notified.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`no-show-reverse-confirm-btn-${it.lesson_event_id}`}
                      onClick={() => reverse(it.lesson_event_id)}
                      disabled={submitting}
                      className="inline-flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" />
                      {submitting ? "Reversing…" : "Yes, undo"}
                    </button>
                    <button
                      type="button"
                      data-testid={`no-show-reverse-cancel-btn-${it.lesson_event_id}`}
                      onClick={() => setConfirmingReversalId(null)}
                      disabled={submitting}
                      className="inline-flex items-center gap-1 text-[12px] font-bold text-slate-600 dark:text-slate-300 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700"
                    >
                      <X className="w-3 h-3" />
                      Keep no-show
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
